import { Loader2, Play, Square } from "lucide-react";

import { Eyebrow } from "~/components/ui/eyebrow";
import type { MeditationVoiceOption } from "~/features/meditations/hooks/use-meditation-voice-options";
import { useVoicePreview } from "~/features/meditations/hooks/use-voice-preview";
import type { SelectableVoice } from "~/features/meditations/types";
import { cn } from "~/lib/utils";

import { MEDITATION_PICKER_GRID, PickerScreen } from "./picker-screen";
import { SelectableCard } from "./selectable-card";

interface VoicePickerScreenProps {
    options: MeditationVoiceOption[];
    selectedVoice: SelectableVoice;
    onSelect: (voice: SelectableVoice) => void;
    onBack: () => void;
}

// AIDEV-NOTE: Accents are the provider's own metadata for these voice ids (ElevenLabs
// /v1/voices, checked 2026-09-09), not a guess from the name: Brad is AUSTRALIAN and Locke
// is AMERICAN, both of which contradict what the names suggest. Michelle Glass's cloned
// voice is described by who she is, not by an accent. Re-check the provider before editing.
const CATALOG_DESCRIPTIONS: Partial<Record<SelectableVoice, string>> = {
    Brad: "Deep and steady, Australian accent",
    Eryn: "Soft and unhurried, American accent",
    Locke: "Warm and slightly raspy, American accent",
    Marcel: "Gentle and tender, French accent",
    Michelle: "Author of the DPMP®, American accent",
};

function voiceDescription(option: MeditationVoiceOption): string {
    if (option.kind === "custom") {
        return option.ownedBySelf ? "Your own voice" : "Their own voice";
    }
    return CATALOG_DESCRIPTIONS[option.value] ?? option.label;
}

function VoiceGroup({
    label,
    options,
    selectedVoice,
    onSelect,
    playingVoice,
    loadingVoice,
    onTogglePreview,
}: {
    label: string;
    options: MeditationVoiceOption[];
    selectedVoice: SelectableVoice;
    onSelect: (voice: SelectableVoice) => void;
    playingVoice: SelectableVoice | null;
    loadingVoice: SelectableVoice | null;
    onTogglePreview: (voice: SelectableVoice) => Promise<void>;
}) {
    if (options.length === 0) return null;
    return (
        <section className="space-y-3">
            <Eyebrow as="div" tone="muted" className="px-1">
                {label}
            </Eyebrow>
            <div
                className={cn("space-y-3 md:space-y-0", MEDITATION_PICKER_GRID)}
            >
                {options.map((option) => {
                    const isPlaying = playingVoice === option.value;
                    const isLoading = loadingVoice === option.value;
                    return (
                        // AIDEV-NOTE: Preview sits OUTSIDE the card, not inside it — the card
                        // is a <button role="radio">, and nesting a second button in it is
                        // invalid HTML and unreachable for keyboard and screen-reader users.
                        <div
                            key={option.value}
                            className="grid grid-cols-[minmax(0,1fr)_44px] items-center gap-3"
                        >
                            <SelectableCard
                                selected={selectedVoice === option.value}
                                onSelect={() => onSelect(option.value)}
                            >
                                <span className="block font-display text-xl font-semibold leading-tight tracking-[-0.01em] text-brand-forest-900">
                                    {option.label}
                                </span>
                                {/* AIDEV-NOTE: No `truncate` — the accent is the point of
                                    this line, and it was the half being clipped. */}
                                <span className="mt-1 block font-body text-base leading-relaxed text-stone-600">
                                    {voiceDescription(option)}
                                </span>
                            </SelectableCard>
                            <button
                                type="button"
                                aria-label={
                                    // Loading is also a stop: the same click cancels it.
                                    isPlaying || isLoading
                                        ? `Stop ${option.label} sample`
                                        : `Preview ${option.label}`
                                }
                                data-testid={`voice-preview-${option.value}`}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    void onTogglePreview(option.value);
                                }}
                                className={cn(
                                    "flex size-11 items-center justify-center rounded-full border transition-colors",
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500 focus-visible:ring-offset-2",
                                    isPlaying
                                        ? "border-brand-forest-700 bg-brand-forest-700 text-white"
                                        : "border-brand-forest-500 text-brand-forest-700 hover:bg-brand-forest-100",
                                )}
                            >
                                {isLoading ? (
                                    <Loader2 className="size-5 animate-spin" />
                                ) : isPlaying ? (
                                    <Square className="size-4 fill-current" />
                                ) : (
                                    <Play className="size-5 fill-current" />
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

export function VoicePickerScreen({
    options,
    selectedVoice,
    onSelect,
    onBack,
}: VoicePickerScreenProps) {
    const custom = options.filter((option) => option.kind === "custom");
    const catalog = options.filter((option) => option.kind === "catalog");
    const { playingVoice, loadingVoice, toggle, stop } = useVoicePreview();

    // AIDEV-NOTE: Leaving the screen must silence the sample — otherwise a preview keeps
    // playing over the meditation the user just started, or under the screen they went back to.
    const selectAndStop = (voice: SelectableVoice) => {
        stop();
        onSelect(voice);
    };
    const backAndStop = () => {
        stop();
        onBack();
    };

    return (
        <PickerScreen
            title="Choose a voice"
            hint="Press play to hear a few seconds of each voice."
            onBack={backAndStop}
            columns="sections"
        >
            <VoiceGroup
                label="Your practitioner"
                options={custom}
                selectedVoice={selectedVoice}
                onSelect={selectAndStop}
                playingVoice={playingVoice}
                loadingVoice={loadingVoice}
                onTogglePreview={toggle}
            />
            <VoiceGroup
                label="Voices"
                options={catalog}
                selectedVoice={selectedVoice}
                onSelect={selectAndStop}
                playingVoice={playingVoice}
                loadingVoice={loadingVoice}
                onTogglePreview={toggle}
            />
        </PickerScreen>
    );
}
